import {
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Text,
  Box,
  Flex
} from '@chakra-ui/react';
import { faChevronDown, faSearch } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge, BadgeVariants, Checkbox } from '@morressier/alembic';
import { useEffect, useMemo, useRef, useState } from 'react';

export interface Option {
  label: string;
  value: string;
}

interface ChakraMultiSelectProps {
  options: Option[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  variant: BadgeVariants;
  customButtonText?: string | React.ReactNode;
  hideClearButton?: boolean;
  customButtonOnClickFunction?: () => void;
  showSelectAllOption?: boolean;
  selectAllLabel?: string;
  showSearch?: boolean;
}

const StickyComponent: React.FC<React.PropsWithChildren<{}>> = ({
  children
}) => {
  return (
    <Box
      position="sticky"
      bottom="0"
      zIndex="docked"
      borderColor="borderSubtle"
      mb={0}
    >
      {children}
    </Box>
  );
};

const ChakraMultiSelect: React.FC<ChakraMultiSelectProps> = ({
  options,
  value,
  onChange,
  customButtonText,
  hideClearButton,
  customButtonOnClickFunction,
  showSelectAllOption = false,
  selectAllLabel = 'Select All',
  placeholder = 'Select',
  variant = 'interactive',
  showSearch = false
}) => {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [menuWidth, setMenuWidth] = useState<number | undefined>(undefined);
  const [searchText, setSearchText] = useState('');

  const handleToggle = (val: string) => {
    if (value.includes(val)) {
      onChange(value.filter(v => v !== val));
    } else {
      onChange([...value, val]);
    }
  };

  const handleClear = () => onChange([]);
  const allOptionValues = options.map(option => option.value);
  const areAllSelected =
    options.length > 0 && allOptionValues.every(option => value.includes(option));

  const handleSelectAllToggle = () => {
    if (areAllSelected) {
      onChange([]);
      return;
    }

    onChange(allOptionValues);
  };

  const filteredOptions = useMemo(() => {
    if (!showSearch || !searchText.trim()) return options;
    const lower = searchText.toLowerCase();
    return options.filter(
      opt =>
        opt.label.toLowerCase().includes(lower) ||
        opt.value.toLowerCase().includes(lower)
    );
  }, [options, searchText, showSearch]);

  useEffect(() => {
    if (buttonRef.current) {
      const { width } = buttonRef.current.getBoundingClientRect();
      setMenuWidth(width);
    }
  }, []);

  return (
    <Menu
      closeOnSelect={false}
      onOpen={() => {
        buttonRef.current?.scrollTo({ top: 0 });
      }}
    >
      <MenuButton
        as={Button}
        rightIcon={<FontAwesomeIcon icon={faChevronDown} />}
        width="100%"
        textAlign="left"
        backgroundColor="surfacePrimaryFill"
        border="1px solid"
        _focus={{ boxShadow: 'outline', bg: 'surfacePrimaryFill' }}
        _hover={{ bg: 'surfacePrimaryFill' }}
        cursor="pointer"
        ref={buttonRef}
        height="auto"
        py={2}
        px={2}
        maxHeight="120px"
        overflowY="auto"
        alignItems="flex-start"
        justifyContent="flex-start"
      >
        <Flex flexWrap="wrap" gap={2} justifyContent="flex-start">
          {value.length === 0 ? (
            <Text textStyle="textBody">{placeholder}</Text>
          ) : (
            options
              .filter(opt => value.includes(opt.value))
              .map(opt => (
                <Badge key={opt.value} variant={variant} isTruncated>
                  {opt.label}
                </Badge>
              ))
          )}
        </Flex>
      </MenuButton>
      <MenuList
        maxHeight="300px"
        overflowY="auto"
        width={menuWidth ? `${menuWidth}px` : 'auto'}
        py={0}
      >
        {showSearch && (
          <Box px={2} pt={2} position="sticky" top={0} zIndex={1} bg="white">
            <InputGroup size="sm">
              <InputLeftElement pointerEvents="none">
                <FontAwesomeIcon icon={faSearch} />
              </InputLeftElement>
              <Input
                placeholder="Search..."
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                onClick={e => e.stopPropagation()}
              />
            </InputGroup>
          </Box>
        )}
        {showSelectAllOption && filteredOptions.length > 0 && (
          <MenuItem
            closeOnSelect={false}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              handleSelectAllToggle();
            }}
          >
            <Box pointerEvents="none">
              <Checkbox
                isChecked={areAllSelected}
                ariaLabel={selectAllLabel}
                id="select-all"
                name={selectAllLabel}
              >
                {selectAllLabel}
              </Checkbox>
            </Box>
          </MenuItem>
        )}
        {filteredOptions.map(option => (
          <MenuItem
            key={option.value}
            closeOnSelect={false}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              handleToggle(option.value);
            }}
          >
            <Box pointerEvents="none">
              <Checkbox
                isChecked={value.includes(option.value)}
                ariaLabel={option.value}
                id={option.value}
                name={option.label}
              >
                {option.label}
              </Checkbox>
            </Box>
          </MenuItem>
        ))}
        {showSearch && filteredOptions.length === 0 && (
          <Box px={4} py={3}>
            <Text textStyle="textCaption" color="textSecondary">
              No results found
            </Text>
          </Box>
        )}

        {!customButtonText ? (
          <>
            {!hideClearButton && (
              <StickyComponent>
                <MenuItem onClick={handleClear}>
                  <Text color="actionSecondaryCautionContent">Clear All</Text>
                </MenuItem>
              </StickyComponent>
            )}
          </>
        ) : (
          <StickyComponent>
            <MenuItem onClick={customButtonOnClickFunction}>
              <Text color="actionSecondaryContent">{customButtonText}</Text>
            </MenuItem>
          </StickyComponent>
        )}
      </MenuList>
    </Menu>
  );
};

export default ChakraMultiSelect;
